import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useCatalogMasterPoll } from './useCatalogMasterPoll';
import { ReelSection } from '../studio/aiReel/ReelSection';
import { reelMessages } from '../studio/aiReel/reelMessages';
import { useOpenSongPage } from '../../../smartLinks/catalog/song/page/useOpenSongPage';
import { CatalogSongReadyLine } from './CatalogSongReadyLine';
import { SongFactRow } from './SongFactRow';
import { SongMasterFailedLine } from './SongMasterFailedLine';
import { SongLyricsFact } from './SongLyricsFact';
import { SongDetailsFact } from './SongDetailsFact';
import { masterFactOf, masterTarget, SongPageTarget } from './songFieldFacts';
import { catalogSongFieldMessages } from './catalogSongFieldMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from '../../../smartLinks/catalog/song/SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogSongFieldProps {
  accountId: number;
  /** The release picked in the studio. Undefined: nothing picked yet. */
  item: CatalogItem | undefined;
  /** True after the create answered `M24_NO_MASTER`: the catalog row lost its master meanwhile. */
  refusedNoMaster?: boolean;
}

/**
 * The song of a Studio asset or reel and of the release page (designs k2, release-kit o1). It only
 * shows the song: the master (the player, or why it is missing), the lyrics and the details that the
 * kit needs. Owner rule 2026-10-06: the master, the lyrics and the details change only on the song
 * page in Catalog, so that the artist sees everything that the song still needs. Each missing or
 * failed part has one button that opens the song page at its section.
 */
const CatalogSongFieldComponent: React.FC<CatalogSongFieldProps> = ({ accountId, item, refusedNoMaster = false }) => {
  const intl = useIntl();
  const title = item?.title ?? '';
  const master = item === undefined ? 'none' : masterFactOf(item, refusedNoMaster);
  useCatalogMasterPoll(accountId, master === 'processing');

  const openSongPage = useOpenSongPage();
  const itemUuid = item?.uuid ?? '';
  const open = useCallback(
    (target: SongPageTarget) => openSongPage(itemUuid, target.section, target.view),
    [itemUuid, openSongPage]
  );

  const hint = useMemo(() => {
    if (item === undefined) {
      return undefined;
    }
    return master === 'none' || master === 'failed'
      ? intl.formatMessage(catalogSongFieldMessages.hintMissing, { title })
      : intl.formatMessage(catalogSongFieldMessages.hintCatalog);
  }, [intl, item, master, title]);

  if (item === undefined) {
    return (
      <ReelSection title={intl.formatMessage(reelMessages.song)}>
        <span className={styles.small}>{intl.formatMessage(catalogSongFieldMessages.noSong)}</span>
      </ReelSection>
    );
  }

  const fixMaster = masterTarget(master);
  const masterAction =
    fixMaster === null
      ? undefined
      : {
          label: intl.formatMessage(
            master === 'failed' ? catalogSongFieldMessages.replaceMaster : catalogSongFieldMessages.addMaster
          ),
          onClick: () => open(fixMaster),
        };

  let masterBody: React.ReactNode = null;
  if (master === 'ready' || master === 'processing') {
    // Processing: the player waits for the AAC copy, and Download works at once (design o1b).
    masterBody = <CatalogSongReadyLine accountId={accountId} item={item} />;
  } else if (master === 'failed') {
    masterBody = <SongMasterFailedLine accountId={accountId} item={item} />;
  }

  return (
    <ReelSection title={intl.formatMessage(reelMessages.song)} hint={hint}>
      <div className="d-flex flex-column gap-3" data-testid="song-field">
        <SongFactRow
          label={intl.formatMessage(catalogSongFieldMessages.masterLabel)}
          text={master === 'none' ? intl.formatMessage(catalogSongFieldMessages.noMaster) : undefined}
          isProblem={master === 'none'}
          action={masterAction}
          testId="song-field-master"
        >
          {masterBody}
        </SongFactRow>
        <SongLyricsFact item={item} onOpen={open} />
        <SongDetailsFact item={item} onOpen={open} />
        <span className={styles.small}>{intl.formatMessage(catalogSongFieldMessages.catalogNote)}</span>
      </div>
    </ReelSection>
  );
};

export const CatalogSongField = TypedMemo(CatalogSongFieldComponent);
